// Loads the Pylon chat widget for organizations with Pylon enabled.
//
// The root layout renders the signed `chat_settings` onto `<body
// data-pylon-chat>` only when Pylon is on for the organization, so this runs
// once per full page load. An organization switch is a full page load, which
// is what re-identifies the widget with the new organization's account.
//
// Our own bubble (`[data-pylon-bubble]`, rendered by the root layout) replaces
// Pylon's, and the support menu's "Chat with support" dispatches `pylon:open`.
const WIDGET_ORIGIN = "https://widget.usepylon.com";

function loadWidget(appId) {
  // The queueing stub from Pylon's install snippet: calls made before the
  // widget script arrives are replayed once it loads.
  const stub = function () {
    stub.e(arguments);
  };
  stub.q = [];
  stub.e = (args) => stub.q.push(args);
  window.Pylon = stub;

  const script = document.createElement("script");
  script.async = true;
  script.src = `${WIDGET_ORIGIN}/widget/${encodeURIComponent(appId)}`;
  document.head.appendChild(script);
}

// The widget's frame briefly outlives a navigation, and the widget reads
// `window.parent.innerWidth`, which by then is the next page's window. Firefox
// lays that page out before its stylesheets arrive and paints it unstyled.
// Removing the frame as the page goes away stops it running. A page kept in
// the back/forward cache is frozen, so its frame stays for when it returns.
function removeFrameOnLeave() {
  window.addEventListener("pagehide", (event) => {
    if (!event.persisted) document.getElementById("pylon-frame")?.remove();
  });
}

function currentTheme() {
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

function followTheme() {
  window.Pylon("setTheme", currentTheme());

  new MutationObserver(() => window.Pylon("setTheme", currentTheme())).observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: ["data-theme"],
    },
  );
}

function bindBubble() {
  const bubble = document.querySelector("[data-pylon-bubble]");

  // Opening the chat brings Pylon's bubble back, where it doubles as the close
  // button, so ours steps aside until the chat closes.
  const open = () => {
    if (bubble) bubble.hidden = true;
    window.Pylon("show");
  };

  bubble?.addEventListener("click", open);
  window.addEventListener("pylon:open", open);

  window.Pylon("onHide", () => {
    window.Pylon("hideChatBubble");
    if (bubble) bubble.hidden = false;
  });
}

function trackUnread() {
  window.Pylon("onChangeUnreadMessagesCount", (count) => {
    document.querySelectorAll("[data-pylon-unread]").forEach((badge) => {
      badge.textContent = count > 9 ? "9+" : String(count);
      badge.hidden = !count;
    });
  });
}

export function startPylon() {
  const raw = document.body.dataset.pylonChat;
  if (!raw) return;

  let settings;
  try {
    settings = JSON.parse(raw);
  } catch {
    return;
  }

  window.pylon = { chat_settings: settings };
  loadWidget(settings.app_id);
  removeFrameOnLeave();
  window.Pylon("hideChatBubble");
  followTheme();
  bindBubble();
  trackUnread();
}
